import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import type { DayAvailability } from '../maintenanceTimeline'
import type { MaintenanceFormatters } from '../useMaintenanceFormatters'
import { HISTORY_RANGES } from './historyRanges'

const EDGE_BAR_COUNT = 4

type AvailabilityBarsProps = {
  days: DayAvailability[]
  formatters: MaintenanceFormatters
}

const getRange = (daysAgo: number) =>
  HISTORY_RANGES.find(({ days }) => daysAgo < days) ??
  HISTORY_RANGES[HISTORY_RANGES.length - 1]

const getTooltipAlignment = (daysAgo: number) => {
  if (daysAgo < EDGE_BAR_COUNT) return 'right-0'
  const range = getRange(daysAgo)
  return daysAgo >= range.days - EDGE_BAR_COUNT
    ? range.leftEdgeTooltipClassName
    : 'left-1/2 -translate-x-1/2'
}

export const AvailabilityBars = memo(function AvailabilityBars({
  days,
  formatters,
}: AvailabilityBarsProps) {
  const { t } = useTranslation()

  return (
    <div className="flex h-10 items-stretch gap-[2px]">
      {days.map(({ day, windows, maintenanceMs }, index) => {
        const daysAgo = days.length - 1 - index
        const hasMaintenance = windows.length > 0
        const summary = hasMaintenance
          ? t('maintenance.bars.total', {
              duration: formatters.formatDuration(maintenanceMs),
            })
          : t('maintenance.bars.none')

        return (
          <div
            key={day.toISOString()}
            role="img"
            aria-label={`${formatters.formatDay(day)}: ${summary}`}
            className={clsx(
              'group relative flex-1',
              getRange(daysAgo).barClassName
            )}
          >
            <div
              className={clsx(
                'h-full w-full rounded-[2px] transition-opacity group-hover:opacity-70',
                hasMaintenance ? 'bg-amber-400' : 'bg-emerald-400/80'
              )}
            />
            <div
              className={clsx(
                'pointer-events-none absolute bottom-full z-10 mb-2 hidden w-60 rounded-md border border-cloudDarker bg-white p-3 text-left text-xs shadow-lg group-hover:block dark:border-darkCharcoal dark:bg-gravel',
                getTooltipAlignment(daysAgo)
              )}
            >
              <p className="font-semibold text-gravel dark:text-snow">
                {formatters.formatDay(day)}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-gravel/80 dark:text-semiWhite">
                <span
                  className={clsx(
                    'h-2 w-2 rounded-full',
                    hasMaintenance ? 'bg-amber-400' : 'bg-emerald-400'
                  )}
                />
                {summary}
              </p>
              {hasMaintenance && (
                <ul className="mt-1.5 space-y-1 pl-3.5 text-gravel/60 dark:text-rock">
                  {windows.map((window) => (
                    <li key={window.id}>{formatters.formatSchedule(window)}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
})
